/**
 * Integration hub contracts (07-connectors.md; 05-api.md "Integrations"): the
 * connector catalogue, installs (credentials are write-only: no schema here has a
 * field that could carry them back), provider routes, health, webhook
 * acknowledgements, and the integrations module's events.
 *
 * Standard event payloads (`connector.event_received`) are carried in their JSON
 * form; their schema lives with the connector SDK (@qrkey/connectors/sdk,
 * `standardEventSchema`), which core consumers use to parse them.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';

/** Mirrors CONNECTOR_CATEGORIES in @qrkey/connectors (which depends on this package). */
export const CONNECTOR_CATEGORIES = [
  'payments',
  'aggregators',
  'delivery',
  'messaging',
  'maps',
] as const;
export type ConnectorCategory = (typeof CONNECTOR_CATEGORIES)[number];
export const connectorCategorySchema = z.enum(CONNECTOR_CATEGORIES);

export const connectorKeySchema = z
  .string()
  .max(40)
  .regex(/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/, {
    message: 'Expected a connector key like messaging-smtp',
  });

export const INSTALL_STATUSES = ['active', 'disabled', 'error'] as const;
export type InstallStatus = (typeof INSTALL_STATUSES)[number];
export const INSTALL_HEALTH = ['ok', 'degraded', 'down'] as const;
export type InstallHealth = (typeof INSTALL_HEALTH)[number];
export const BREAKER_STATES = ['closed', 'open', 'half_open'] as const;

// ---------------------------------------------------------------------------
// Catalogue: GET /v1/connectors

export const connectorSchema = z.object({
  key: connectorKeySchema,
  category: connectorCategorySchema,
  version: z.string(),
  displayName: z.string(),
  capabilities: z.array(z.string()),
  webhooks: z.array(z.object({ path: z.string(), events: z.array(z.string()) })),
  healthCheck: z.boolean(),
  timeoutMs: z.int(),
  /** Test/dev connector; never for a live outlet. */
  sandbox: z.boolean(),
  /** JSON Schema of the settings form. */
  settingsSchema: z.record(z.string(), z.unknown()),
  /** Names of the credential fields (write-only). */
  credentialFields: z.array(z.object({ name: z.string(), required: z.boolean() })),
});
export type ConnectorDescription = z.output<typeof connectorSchema>;

export const connectorListSchema = z.object({ data: z.array(connectorSchema) });

// ---------------------------------------------------------------------------
// Installs: /v1/connector-installs

/** Non-secret settings: a JSON object validated by the connector's settingsSchema. */
export const connectorSettingsSchema = z.record(z.string(), z.unknown());

/**
 * Write-only credentials: string values only (API keys, secrets, passwords), at most
 * 20 fields of 4 KB. Validated by the connector's credentialsSchema, stored in the
 * vault, never returned, logged, audited or stored for an idempotent replay.
 */
export const connectorCredentialsSchema = z
  .record(z.string().min(1).max(64), z.string().max(4_096))
  .refine((r) => Object.keys(r).length <= 20, { message: 'At most 20 credential fields' });

export const connectorInstallSchema = z.object({
  id: uuidSchema,
  connectorKey: connectorKeySchema,
  category: connectorCategorySchema,
  /** null = company-wide. */
  outletId: uuidSchema.nullable(),
  settings: connectorSettingsSchema,
  status: z.enum(INSTALL_STATUSES),
  health: z.enum(INSTALL_HEALTH),
  healthDetail: z.string().nullable(),
  lastHealthAt: isoTimestampSchema.nullable(),
  /** Changes every time the credentials are written; reveals nothing about them. */
  credentialsVersion: uuidSchema,
  credentialsUpdatedAt: isoTimestampSchema,
  sandbox: z.boolean(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type ConnectorInstallView = z.output<typeof connectorInstallSchema>;

export const connectorInstallListSchema = z.object({ data: z.array(connectorInstallSchema) });

export const connectorInstallListQuerySchema = z.object({
  outletId: uuidSchema.optional().describe('Only installs of this outlet (and company-wide ones)'),
  category: connectorCategorySchema.optional(),
  includeDisabled: z.enum(['true', 'false']).optional(),
});

export const createConnectorInstallSchema = z.object({
  connectorKey: connectorKeySchema,
  /** Omit or null for a company-wide install. */
  outletId: uuidSchema.nullable().optional(),
  settings: connectorSettingsSchema.default({}),
  credentials: connectorCredentialsSchema,
});

export const updateConnectorInstallSchema = z
  .object({
    settings: connectorSettingsSchema.optional(),
    status: z.enum(['active', 'disabled']).optional(),
  })
  .refine((b) => b.settings !== undefined || b.status !== undefined, {
    message: 'Send settings or status',
  });

export const rotateCredentialsSchema = z.object({ credentials: connectorCredentialsSchema });

export const connectorInstallParamsSchema = z.object({ installId: uuidSchema });

export const connectorTestResultSchema = z.object({
  installId: uuidSchema,
  ok: z.boolean(),
  health: z.enum(INSTALL_HEALTH),
  detail: z.string(),
  durationMs: z.int().min(0),
});

export const connectorHealthSchema = z.object({
  installId: uuidSchema,
  status: z.enum(INSTALL_STATUSES),
  health: z.enum(INSTALL_HEALTH),
  healthDetail: z.string().nullable(),
  lastHealthAt: isoTimestampSchema.nullable(),
  /** This API instance's circuit breaker for the install. */
  breaker: z.enum(BREAKER_STATES),
  /** Outbound calls in the last 24 hours, by status (`200`, `timeout`, …). */
  calls24h: z.record(z.string(), z.int()),
});

// ---------------------------------------------------------------------------
// Provider routes: {o}/provider-routes/{category}

export const providerRouteParamsSchema = z.object({
  outletId: uuidSchema,
  category: connectorCategorySchema,
});

export const setProviderRouteSchema = z.object({
  /** Installs in preference order: the first usable one is used, then the next. */
  installIds: z
    .array(uuidSchema)
    .max(10)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Each install at most once' }),
});

export const providerRouteSchema = z.object({
  outletId: uuidSchema,
  category: connectorCategorySchema,
  installIds: z.array(uuidSchema),
  updatedAt: isoTimestampSchema.nullable(),
  version: z.int(),
});

// ---------------------------------------------------------------------------
// Webhooks in: POST /hooks/{connector}/{installId}

export const webhookParamsSchema = z.object({
  connector: z.string().max(60),
  installId: z.string().max(60),
});

/** The default acknowledgement (a connector may answer with its partner's own shape). */
export const webhookAckSchema = z.object({ received: z.literal(true) });

// ---------------------------------------------------------------------------
// Events

const installRef = {
  installId: uuidSchema,
  connectorKey: connectorKeySchema,
  category: connectorCategorySchema,
};

export const connectorInstalledV1 = defineEvent(
  'connector.installed',
  1,
  z.object({ ...installRef, outletId: uuidSchema.nullable(), credentialsVersion: uuidSchema }),
);

export const connectorSettingsChangedV1 = defineEvent(
  'connector.settings_changed',
  1,
  z.object({ ...installRef, settingKeys: z.array(z.string()) }),
);

export const connectorCredentialsRotatedV1 = defineEvent(
  'connector.credentials_rotated',
  1,
  z.object({ ...installRef, credentialsVersion: uuidSchema }),
);

export const connectorStatusChangedV1 = defineEvent(
  'connector.status_changed',
  1,
  z.object({ ...installRef, status: z.enum(INSTALL_STATUSES) }),
);

export const connectorHealthChangedV1 = defineEvent(
  'connector.health_changed',
  1,
  z.object({
    ...installRef,
    health: z.enum(INSTALL_HEALTH),
    previous: z.enum(INSTALL_HEALTH),
    detail: z.string().nullable(),
  }),
);

export const providerRouteChangedV1 = defineEvent(
  'provider_route.changed',
  1,
  z.object({ category: connectorCategorySchema, installIds: z.array(uuidSchema) }),
);

/** A webhook was stored and its signature verified; processing follows in the worker. */
export const connectorWebhookReceivedV1 = defineEvent(
  'connector.webhook_received',
  1,
  z.object({ ...installRef, inboxId: uuidSchema, dedupeKey: z.string() }),
);

/** A webhook was stored but refused: bad signature (an alert) or unparseable payload. */
export const connectorWebhookRejectedV1 = defineEvent(
  'connector.webhook_rejected',
  1,
  z.object({
    ...installRef,
    inboxId: uuidSchema,
    reason: z.enum(['SIGNATURE_INVALID', 'PARSE_FAILED']),
  }),
);

/**
 * One standard event parsed from a webhook (07-connectors.md): what core modules
 * react to. `event` is the JSON form of a StandardEvent (@qrkey/connectors/sdk);
 * `idempotencyKey` (stable across partner retries) is what core commands use.
 */
export const connectorEventReceivedV1 = defineEvent(
  'connector.event_received',
  1,
  z.object({
    ...installRef,
    inboxId: uuidSchema,
    index: z.int().min(0),
    eventType: z.string(),
    idempotencyKey: z.string(),
    event: z.record(z.string(), z.unknown()),
  }),
);

/** A verified delivery was parsed; its standard events (possibly none) were emitted. */
export const connectorWebhookProcessedV1 = defineEvent(
  'connector.webhook_processed',
  1,
  z.object({ ...installRef, inboxId: uuidSchema, eventTypes: z.array(z.string()) }),
);

export const INTEGRATIONS_EVENTS = [
  connectorInstalledV1,
  connectorSettingsChangedV1,
  connectorCredentialsRotatedV1,
  connectorStatusChangedV1,
  connectorHealthChangedV1,
  providerRouteChangedV1,
  connectorWebhookReceivedV1,
  connectorWebhookRejectedV1,
  connectorWebhookProcessedV1,
  connectorEventReceivedV1,
] as const;
